<template>
  <div class="ly-bar">
    <!-- первая строка: крошки (их кладёт страница через слот crumbs, как П 6641) и камера -->
    <div class="ly-bar-row">
      <slot name="crumbs"><span class="ly-who">{{ s.label }}</span></slot>
      <span class="ly-sp"></span>
      <button type="button" class="tgl ly-ghost" data-help="слои/фронт" @click="act('front')">фронт</button>
      <button type="button" class="tgl ly-ghost" data-help="слои/торец" @click="act('side')">
        {{ s.sideView ? '↩ из торца' : 'вдоль стопки' }}
      </button>
    </div>

    <!-- вторая строка: подача ряда. Порядок × шаг — два двоичных решения вместо четырёх ярлыков. -->
    <div class="ly-bar-row">
      <span class="ly-grp">
        <span class="tlabel">порядок</span>
        <button type="button" class="tgl" :aria-pressed="String(zo === 't')" data-help="порядок/по времени"
                @click="act('zMode', zOf('t', zs))">по времени</button>
        <button type="button" class="tgl" :aria-pressed="String(zo === 'p')" data-help="порядок/по фразам"
                @click="act('zMode', zOf('p', zs))">по фразам</button>
      </span>
      <span class="ly-grp">
        <span class="tlabel">шаг</span>
        <!-- При сортировке шаг заперт. aria-disabled, а не disabled: на отключённую кнопку
             браузер не шлёт событий мыши, и подсказка «почему нельзя» не всплывала бы. -->
        <button v-for="st in STEPS" :key="st.k" type="button" class="tgl"
                :class="{ 'ly-lock': s.sortOn }" :aria-disabled="s.sortOn ? 'true' : null"
                :aria-pressed="String(!s.sortOn && zs === st.k)"
                :data-help="s.sortOn ? 'шаг/заперт' : st.help"
                @click="!s.sortOn && act('zMode', zOf(zo, st.k))">{{ st.t }}</button>
        <span v-if="s.sortOn" class="tlabel">при сортировке шаг всегда ровный</span>
      </span>
      <span v-if="s.sortBlock !== null" class="ly-grp">
        <span class="tlabel">сортировка</span>
        <b class="ly-sortlab">{{ s.blockName }} {{ s.sortDir === 1 ? '↑ от пустых' : '↓ от максимума' }}</b>
        <button type="button" class="tgl ly-ghost" data-help="сортировка/снять" @click="act('unsort')">× снять</button>
      </span>
      <span class="ly-grp">
        <span class="tlabel">слои</span>
        <button type="button" class="tgl" :aria-pressed="String(s.layerView === 'columns')" data-help="слои/столбцы"
                @click="act('layerView', 'columns')">столбцы</button>
        <button type="button" class="tgl" :aria-pressed="String(s.layerView === 'spines')" data-help="слои/корешки"
                @click="act('layerView', 'spines')">корешки</button>
      </span>
      <span class="ly-grp">
        <span class="tlabel">подписи</span>
        <button v-for="p in PH" :key="p.k" type="button" class="tgl" :aria-pressed="String(s.phMode === p.k)"
                :data-help="'подписи/' + p.t" @click="act('phMode', p.k)">{{ p.t }}</button>
      </span>
      <!-- события рисуют LayerEventMarks и LayerEvents; здесь только выбор режима -->
      <span class="ly-grp">
        <span class="tlabel">события</span>
        <button v-for="e in EV" :key="e.k" type="button" class="tgl" :aria-pressed="String(s.evMode === e.k)"
                :data-help="'события/' + e.t" @click="act('evMode', e.k)">{{ e.t }}</button>
      </span>
      <span v-if="s.timeLike" class="ly-grp">
        <span class="tlabel">в один день</span>
        <button type="button" class="tgl" :aria-pressed="String(s.dayRow)"
                data-help="день/в ряд" @click="act('dayRow')">в ряд</button>
      </span>
      <span v-if="s.maxScroll >= 40" class="ly-grp ly-grp-nav">
        <span class="tlabel">пролёт</span>
        <span class="ly-strack"><i :style="{ width: nav.pct + '%' }"></i></span>
        <span class="tlabel ly-sinfo"><b v-if="nav.day">{{ nav.day }}</b>{{ nav.text }}</span>
        <button type="button" class="tgl ly-ghost" data-help="пролёт/в начало" @click="act('toStart')">в начало</button>
      </span>
      <span class="ly-grp">
        <span class="tlabel">двоичная мера</span>
        <button type="button" class="tgl" :aria-pressed="String(s.sortHit)" data-help="мера/блок синей"
                @click="act('cycleHit')">блок оттока</button>
        <!-- медиана имеет смысл только при сортировке: без неё ряд хронологический -->
        <button v-if="s.sortOn" type="button" class="tgl" :aria-pressed="String(s.medMark)" data-help="мера/медиана"
                @click="act('medMark')">медиана</button>
        <template v-if="s.sortOn && s.medMark">
          <button type="button" class="tgl" :aria-pressed="String(s.medAxis === 'v')" data-help="медиана/вертикальная"
                  @click="act('medAxis', 'v')">вертикальная</button>
          <button type="button" class="tgl" :aria-pressed="String(s.medAxis === 'h')" data-help="медиана/горизонтальная"
                  @click="act('medAxis', 'h')">горизонтальная</button>
          <button v-for="m in MB" :key="m.k" type="button" class="tgl" :aria-pressed="String(s.medBase === m.k)"
                  :data-help="'медиана/' + m.t" @click="act('medBase', m.k)">{{ m.t }}</button>
          <button type="button" class="tgl ly-ghost" data-help="медиана/к медиане" @click="act('toMed')">↳ к медиане</button>
        </template>
      </span>
      <!-- Тексты и разбор появляются только при выбранном блоке среза; сами флаги и окна
           рисуют LayerFlags и LayerAnalysis по состоянию сцены. -->
      <template v-if="s.storyBlock !== null">
        <span class="ly-grp">
          <span class="tlabel">тексты</span>
          <button v-for="t in TX" :key="t.k" type="button" class="tgl" :aria-pressed="String(s.storyMode === t.k)"
                  :data-help="t.help" @click="act('storyMode', t.k)">{{ t.t }}</button>
          <button type="button" class="tgl" :aria-pressed="String(s.anWords)" data-help="тексты/слова на флаге"
                  @click="act('anWords')">слова на флаге</button>
          <button type="button" class="tgl" :aria-pressed="String(s.anInf)" data-help="тексты/влияние"
                  @click="act('anInf')">влияние</button>
          <button type="button" class="tgl" :aria-pressed="String(s.anDist)" data-help="тексты/распределения"
                  @click="act('anDist')">распределения</button>
        </span>
        <span class="ly-grp">
          <span class="tlabel">разбор</span>
          <button type="button" class="tgl" :aria-pressed="String(s.anStroy)" data-help="разбор/строй"
                  @click="act('anStroy')">строй</button>
          <button type="button" class="tgl" :aria-pressed="String(s.anKoro)" data-help="разбор/коромысло"
                  @click="act('anKoro')">коромысло</button>
          <button type="button" class="tgl" :aria-pressed="String(s.anGrid)" data-help="разбор/решётка"
                  @click="act('anGrid')">решётка</button>
          <template v-if="s.anStroy || s.anKoro || s.anGrid">
            <span class="tlabel">признак</span>
            <button v-for="a in AT" :key="a.k" type="button" class="tgl" :aria-pressed="String(s.anAttr === a.k)"
                    :data-help="'признак/' + a.t" @click="act('anAttr', a.k)">{{ a.t }}</button>
          </template>
        </span>
      </template>
      <span class="ly-sp"></span>
      <button v-if="s.timeLike && !s.sortOn" type="button" class="tgl" :aria-pressed="String(s.railOn)"
              data-help="низ/линейка" @click="act('railOn')">линейка</button>
      <button type="button" class="tgl" :aria-pressed="String(s.tlOn)" data-help="низ/лента"
              @click="act('tlOn')">лента времени</button>
      <button type="button" class="tgl" :aria-pressed="String(s.f6On)" data-help="низ/ф6"
              @click="act('f6On')">Ф6 · вариабельность</button>
    </div>
  </div>
</template>

<script>
// Две строки управления слоями (buildLayerBar прототипа). Состояние живёт в LayerScene:
// панель только показывает его и отдаёт действие наверх одним событием act(имя, значение).
const zORD = { time: 't', even: 't', phrase: 'p', phtime: 'p' };
const zSTP = { time: 'd', phtime: 'd', even: 'r', phrase: 'r' };

export default {
  name: 'LayerBar',
  props: {
    s: { type: Object, required: true },     // снимок состояния сцены
    nav: { type: Object, required: true },   // {pct, day, text} — пролёт; реактивный объект, меняется покадрово
  },
  emits: ['act'],
  created() {
    this.STEPS = [{ k: 'd', t: 'по датам', help: 'шаг/по датам' }, { k: 'r', t: 'ровный', help: 'шаг/ровный' }];
    this.PH = [{ k: 'off', t: 'нет' }, { k: 'chips', t: 'чипы' }, { k: 'lanes', t: 'дорожки' }, { k: 'mix', t: 'состав' }];
    this.EV = [{ k: 'off', t: 'нет' }, { k: 'pins', t: 'вехи' }, { k: 'score', t: 'лента' }, { k: 'tint', t: 'заливка' },
      { k: 'card', t: 'картотека' }, { k: 'joints', t: 'стыки' }, { k: 'funnel', t: 'воронка' }];
    this.MB = [{ k: 'col', t: 'столбец' }, { k: 'grp', t: 'группа' }, { k: 'all', t: 'все слова' }];
    this.TX = [{ k: 'plates', t: 'плашки в ряду', help: 'тексты/плашки' },
      { k: 'panel', t: 'панель чтения', help: 'тексты/панель' }, { k: 'both', t: 'вместе', help: 'тексты/вместе' }];
    this.AT = [{ k: 'none', t: 'нет' }, { k: 'dev', t: 'устройство' }, { k: 'phrase', t: 'фраза' }];
  },
  computed: {
    zo() { return zORD[this.s.zMode] || 't'; },
    zs() { return zSTP[this.s.zMode] || 'd'; },
  },
  methods: {
    zOf(o, st) { return o === 't' ? (st === 'd' ? 'time' : 'even') : (st === 'd' ? 'phtime' : 'phrase'); },
    act(name, value) { this.$emit('act', name, value); },
  },
};
</script>

<style scoped>
.ly-bar { display: flex; flex-direction: column; gap: 10px; margin: -4px 0 14px; }
/* зазоры как в П (6684–6771): ряд 6, группа ещё +10, подпись группы +2 до кнопок, пролёт 8 и +14 */
.ly-bar-row { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
.ly-grp { display: inline-flex; gap: 6px; align-items: center; flex: 0 0 auto; flex-wrap: wrap; }
.ly-grp:not(:first-child) { margin-left: 10px; }
.ly-grp > .tlabel:first-child { margin-right: 2px; }
.ly-grp.ly-grp-nav { gap: 8px; margin-left: 14px; }
.ly-sp { flex: 1 1 auto; }
.ly-who { font-size: 14px; font-weight: 700; color: var(--accent); }
.ly-bar .tgl { padding: 5px 10px; font-size: 12.5px; }
.ly-bar .tgl.ly-ghost { color: var(--text-muted); }
.ly-bar .tgl.ly-lock { opacity: .4; cursor: not-allowed; }
.ly-sortlab { font-size: 12px; color: var(--accent); }
.ly-strack { display: block; width: 140px; height: 5px; border-radius: 3px; background: var(--grid); position: relative; }
.ly-strack i { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 3px; background: var(--accent); }
.ly-sinfo { font-variant-numeric: tabular-nums; min-width: 110px; }
.ly-sinfo b { color: var(--text-primary); }
</style>
